/** Shared web/native service contract. Money strings preserve backend decimal values. */
export type Trade = "siding" | "windows" | "gutters" | "fencing" | "repair";
export type Unit =
  | "ea"
  | "lf"
  | "sf"
  | "sq"
  | "item"
  | "opening"
  | "run"
  | "hour";
export interface Discount {
  type: "amount" | "percent";
  value: string;
}
export interface Specification {
  label: string;
  value: string;
}
export interface ServiceWorksheet {
  kind: "siding" | "windows" | "gutters" | "fencing";
  gross?: string;
  deductions?: string;
  waste_percent?: string;
  include_waste?: boolean;
  length?: string;
  gate_width?: string;
  deduct_gates?: boolean;
  width?: string;
  height?: string;
  installation?: "insert" | "full_frame";
  custom_price?: boolean;
  size_bands?: { max_width: string; max_height: string; unit_price: string }[];
}
export const worksheetFields: Record<
  ServiceWorksheet["kind"],
  { key: keyof ServiceWorksheet; label: string; toggle?: boolean }[]
> = {
  siding: [
    { key: "gross", label: "Gross wall area (sq ft)" },
    { key: "deductions", label: "Opening deductions (sq ft)" },
    { key: "waste_percent", label: "Waste (%)" },
    {
      key: "include_waste",
      label: "Include waste in selling quantity",
      toggle: true,
    },
  ],
  windows: [
    { key: "width", label: "Opening width (inches)" },
    { key: "height", label: "Opening height (inches)" },
    {
      key: "custom_price",
      label: "Use manually entered price instead of size bands",
      toggle: true,
    },
  ],
  gutters: [{ key: "length", label: "Run length (feet)" }],
  fencing: [
    { key: "length", label: "Run length including gate openings (feet)" },
    { key: "gate_width", label: "Total gate openings in this run (feet)" },
    {
      key: "deduct_gates",
      label: "Deduct gate openings from priced fence length",
      toggle: true,
    },
  ],
};
/** Display-only arithmetic. Backend validates and recalculates before saving/signing. */
export function worksheetQuantity(line: ServiceLine): string {
  const s = line.worksheet;
  if (!s || s.kind === "windows") return line.quantity;
  if (s.kind === "siding")
    return (
      ((Number(s.gross || 0) - Number(s.deductions || 0)) *
        (s.include_waste ? 1 + Number(s.waste_percent || 0) / 100 : 1)) /
      (line.unit === "sq" ? 100 : 1)
    ).toFixed(3);
  return (
    Number(s.length || 0) -
    (s.kind === "fencing" && s.deduct_gates !== false
      ? Number(s.gate_width || 0)
      : 0)
  ).toFixed(3);
}
export interface ServiceLine {
  key: string;
  name: string;
  description: string;
  quantity: string;
  unit: Unit;
  unit_price: string | null;
  taxable: boolean;
  discountable?: boolean;
  optional: boolean;
  selected: boolean;
  is_fee: boolean;
  discount?: Discount;
  product_name: string;
  manufacturer: string;
  sku: string;
  specifications: Specification[];
  requires_order_verification: boolean;
  worksheet?: ServiceWorksheet;
  minimum_unit_price?: string | null;
  internal_unit_cost?: string | null;
  catalog_item?: number | null;
  quantity_rule?: {
    source_line: string;
    per_quantity: string;
    multiplier: string;
    extra: string;
    round_up: boolean;
  };
  quantity_explanation?: string;
  source?: {
    label: string;
    record_id?: number;
    revision?: string;
    original_quantity?: string;
    unit: Unit;
    manually_overridden: boolean;
    confirmed: boolean;
  };
}
export interface ServiceOption {
  key: string;
  name: string;
  description: string;
  lines: ServiceLine[];
  discount?: Discount;
}
export interface ServiceScope {
  key: string;
  trade: Trade;
  name: string;
  location: string;
  mode: "install" | "replace" | "repair";
  optional: boolean;
  exclusive_group?: string;
  selected_option: string | null;
  options: ServiceOption[];
  terms: string;
  warranty: string;
  specifications: Specification[];
  template_version?: number | null;
}
export interface ServiceConfiguration {
  version: 2;
  include_roofing: boolean;
  services: ServiceScope[];
  discount?: Discount;
  project_charges?: ServiceLine[];
  /** Starting page visibility only; existing proposals use their own page records. */
  page_defaults?: Partial<Record<'title' | 'introduction' | 'inspection' | 'measurements' | 'manufacturer', boolean>>;
}
export type ServiceSelections = Record<
  string,
  { option: string | null; lines: string[] }
>;
export type PricedLine = Omit<ServiceLine, "discount"> & {
  service_key: string;
  gross: string;
  discount: string;
  net: string;
  tax: string;
  total: string;
};
export interface ServiceTotals {
  subtotal: string;
  discount: string;
  net: string;
  tax: string;
  total: string;
}
export interface AcceptedService extends ServiceTotals {
  key: string;
  trade: Trade;
  name: string;
  location: string;
  mode: string;
  option: { key: string; name: string; description: string };
  terms: string;
  warranty: string;
  specifications: Specification[];
  lines: PricedLine[];
}
export interface ServiceQuote extends ServiceTotals {
  version: 2;
  services: AcceptedService[];
  options?: Record<string, ServiceTotals>;
  lines?: PricedLine[];
}
export interface ServiceTemplate {
  id: number;
  name: string;
  folder: string;
  is_favorite: boolean;
  is_archived: boolean;
  current_version: number;
  version_id: number | null;
  configuration: ServiceConfiguration | null;
  version_ids?: number[];
}
export const tradeNames: Record<Trade, string> = {
  siding: "Siding",
  windows: "Windows",
  gutters: "Gutters",
  fencing: "Fencing",
  repair: "Repair work",
};
let counter = 0;
export const serviceKey = () =>
  `s-${Date.now().toString(36)}-${(++counter).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
export function newLine(
  name = "Additional work",
  unit: Unit = "item",
): ServiceLine {
  return {
    key: serviceKey(),
    name,
    description: "",
    quantity: "1",
    unit,
    unit_price: null,
    taxable: true,
    discountable: true,
    optional: false,
    selected: false,
    is_fee: false,
    product_name: "",
    manufacturer: "",
    sku: "",
    specifications: [],
    requires_order_verification: false,
  };
}
/** A bundle owns shared charges once; never overwrite an already edited charge. */
export function appendTemplate(
  config: ServiceConfiguration,
  template: ServiceTemplate,
): ServiceConfiguration {
  if (!template.configuration) return config;
  const incoming = template.configuration;
  const fees = new Map(
    (config.project_charges || []).map((line) => [line.key, line]),
  );
  for (const line of incoming.project_charges || []) {
    if (!fees.has(line.key))
      fees.set(line.key, JSON.parse(JSON.stringify(line)));
  }
  return {
    ...config,
    include_roofing: config.include_roofing || incoming.include_roofing,
    services: [
      ...config.services,
      ...incoming.services.map((service) => ({
        ...duplicateService(service),
        template_version: template.version_id,
      })),
    ],
    project_charges: [...fees.values()],
    discount: config.discount || incoming.discount,
  };
}

export function applyMeasurement(
  line: ServiceLine,
  source: NonNullable<ServiceLine["source"]>,
): ServiceLine {
  // Suggestions are deliberately unconfirmed until the rep checks actual gutter edges.
  return {
    ...line,
    quantity: source.original_quantity || "0",
    quantity_rule: undefined,
    worksheet: { kind: "gutters", length: source.original_quantity || "0" },
    unit: "lf",
    source: { ...source, confirmed: false, manually_overridden: false },
  };
}
export function startingService(trade: Trade, variant = ""): ServiceScope {
  const labels: Record<Trade, string[]> = {
    siding: [
      "Material / profile",
      "Color",
      "Removal included",
      "Waste allowance",
    ],
    windows: [
      "Installation method",
      "Product series",
      "Frame color",
      "Glass package",
    ],
    gutters: ["Profile / size", "Color", "Removal included"],
    fencing: [
      "Material / style",
      "Height",
      "Removal included",
      "Slope / installation",
    ],
    repair: ["System", "Issue found", "Included solution"],
  };
  const line = newLine(
    {
      siding: "Siding coverage",
      windows: "Window opening",
      gutters: "Gutter run",
      fencing: "Fence run",
      repair: "Repair task",
    }[trade],
    {
      siding: "sf",
      windows: "opening",
      gutters: "lf",
      fencing: "lf",
      repair: "item",
    }[trade] as Unit,
  );
  const lineLabels: Record<Trade, string[]> = {
    siding: [
      "Elevation / location",
      "Gross area (sq ft)",
      "Opening deductions (sq ft)",
      "Waste (%)",
    ],
    windows: [
      "Room / location",
      "Width (in)",
      "Height (in)",
      "Style",
      "Final ordering dimensions verified",
    ],
    gutters: ["Run / location", "Stories / access"],
    fencing: [
      "Run / location",
      "Corners / end conditions",
      "Post spacing (ft)",
    ],
    repair: ["Location", "Issue", "Solution"],
  };
  line.specifications = lineLabels[trade].map((label) => ({
    label,
    value: "",
  }));
  line.requires_order_verification = trade === "windows" || trade === "fencing";
  if (trade !== "repair")
    line.worksheet = {
      kind: trade,
      ...(trade === "windows"
        ? { installation: variant.includes("Full") ? "full_frame" : "insert" }
        : {}),
    };
  const option: ServiceOption = {
    key: serviceKey(),
    name: variant || "Included scope",
    description: "",
    lines: [line],
  };
  return {
    key: serviceKey(),
    trade,
    name: variant ? `${tradeNames[trade]} · ${variant}` : tradeNames[trade],
    location: "",
    mode: trade === "repair" ? "repair" : "replace",
    optional: false,
    selected_option: option.key,
    options: [option],
    terms: "",
    warranty: "",
    specifications: labels[trade].map((label, index) => ({
      label,
      value: index === 0 ? variant : "",
    })),
  };
}
export const starterTypes: { trade: Trade; variant: string }[] = [
  { trade: "siding", variant: "Vinyl" },
  { trade: "siding", variant: "Fiber cement" },
  { trade: "windows", variant: "Insert replacement" },
  { trade: "windows", variant: "Full-frame replacement" },
  { trade: "gutters", variant: "Seamless gutters" },
  { trade: "fencing", variant: "Wood privacy" },
  { trade: "fencing", variant: "Vinyl privacy" },
  { trade: "fencing", variant: "Chain-link" },
  { trade: "fencing", variant: "Aluminum / ornamental" },
  { trade: "repair", variant: "Repair scope" },
];
export function duplicateService(service: ServiceScope): ServiceScope {
  const copy = JSON.parse(JSON.stringify(service)) as ServiceScope;
  copy.key = serviceKey();
  return copy;
}
export function defaultSelections(
  configuration: ServiceConfiguration,
): ServiceSelections {
  const groups = new Set<string>();
  return Object.fromEntries(
    configuration.services.map((service) => {
      let option = service.selected_option;
      if (service.exclusive_group && groups.has(service.exclusive_group))
        option = null;
      if (option && service.exclusive_group)
        groups.add(service.exclusive_group);
      return [
        service.key,
        {
          option,
          lines:
            service.options
              .find((row) => row.key === option)
              ?.lines.filter((row) => row.selected)
              .map((row) => row.key) || [],
        },
      ];
    }),
  );
}
export function chooseService(
  configuration: ServiceConfiguration,
  selections: ServiceSelections,
  key: string,
  option: string | null,
): ServiceSelections {
  const service = configuration.services.find((row) => row.key === key)!;
  const next = { ...selections };
  if (option && service.exclusive_group)
    for (const other of configuration.services) {
      if (other.exclusive_group === service.exclusive_group)
        next[other.key] = { option: null, lines: [] };
    }
  next[key] = {
    option,
    lines:
      service.options
        .find((row) => row.key === option)
        ?.lines.filter((row) => row.selected)
        .map((row) => row.key) || [],
  };
  return next;
}

/** Copy configured product/pricing without overwriting measured quantities or locations. */
export function applyProductToLines(
  lines: ServiceLine[],
  sourceKey: string,
  selectedKeys: string[],
): ServiceLine[] {
  const source = lines.find((line) => line.key === sourceKey);
  if (!source) return lines;
  return lines.map((line) =>
    selectedKeys.includes(line.key) && line.unit === source.unit
      ? {
          ...line,
          product_name: source.product_name,
          manufacturer: source.manufacturer,
          sku: source.sku,
          unit_price: source.unit_price,
          minimum_unit_price: source.minimum_unit_price,
          description: source.description,
          worksheet:
            line.worksheet && source.worksheet?.kind === line.worksheet.kind
              ? {
                  ...line.worksheet,
                  installation: source.worksheet.installation,
                  size_bands: source.worksheet.size_bands,
                  custom_price: source.worksheet.custom_price,
                }
              : line.worksheet,
        }
      : line,
  );
}

export function templateChanges(
  before: ServiceScope,
  after: ServiceScope,
): string[] {
  const oldLines = before.options.flatMap((option) => option.lines);
  const changes = after.options.flatMap((option) =>
    option.lines.map((line) => {
      const old = oldLines.find((row) => row.key === line.key);
      return old
        ? `${line.name}: ${old.quantity} ${old.unit} at $${old.unit_price ?? "unpriced"} → ${line.quantity} ${line.unit} at $${line.unit_price ?? "unpriced"}`
        : `Add ${line.name}: ${line.quantity} ${line.unit} at $${line.unit_price ?? "unpriced"}`;
    }),
  );
  for (const old of oldLines)
    if (
      !after.options.some((option) =>
        option.lines.some((line) => line.key === old.key),
      )
    )
      changes.push(`Remove ${old.name}`);
  if (before.terms !== after.terms)
    changes.push("Service terms / exclusions changed.");
  if (before.warranty !== after.warranty) changes.push("Warranty changed.");
  return changes;
}

export function sellingQuantity(
  line: ServiceLine,
  lines: ServiceLine[],
): string {
  const rule = line.quantity_rule;
  const source = rule && lines.find((row) => row.key === rule.source_line);
  if (!rule || !source || Number(rule.per_quantity) <= 0)
    return worksheetQuantity(line);
  let count = Number(worksheetQuantity(source)) / Number(rule.per_quantity);
  if (rule.round_up) count = Math.ceil(count);
  return (
    count * Number(rule.multiplier || 1) +
    Number(rule.extra || 0)
  ).toFixed(3);
}
